<template>
  <div>
    <div class="rounded-lg shadow bg-white p-4">
      <div class="flex flex-wrap justify-between items-center gap-2 mb-3">
        <div>
          <div class="font-bold text-xl">카톡 관리</div>
        </div>
        <div class="inline-flex gap-2">
          <Button :type="ButtonTypes.blue" label="결과값 세팅하기" :onClick="openSettingModal" />
          <Button :type="ButtonTypes.green" label="엑셀 다운로드" :onClick="downloadExcel" :loading="loading.excel" />
        </div>
      </div>

      <div class="inline-flex gap-2 justify-start items-center my-3 flex-wrap">
        <VueDatePicker
          v-model="selectedDate"
          range
          :teleport="true"
          format="yyyy-MM-dd"
          :enable-time-picker="false"
          :clearable="true"
          multi-calendars
          placeholder="검색 시작일, 검색 종료일"
          class="!w-[400px]"
        />
        <b-input-group class="!w-[520px]">
          <b-form-input
            placeholder="이름 또는 학생코드, 전화번호를 입력하세요."
            v-model="keyword"
            v-on:keyup.enter="search"
          />
          <b-input-group-append>
            <button class="btn btn-primary" v-on:click="search">검색</button>
          </b-input-group-append>
        </b-input-group>
      </div>

      <div class="text-sm text-gray-500 mb-4">
        결과값 조합: {{ selectedOptionLabels.join(' / ') }}
      </div>

      <b-table :fields="fields" :items="items" class="text-left responsive">
        <template #cell(createdAt)="data">
          {{ formatCreatedAt(data.item.createdAt) }}
        </template>
        <template #cell(accessCode)="data">
          <button class="btn btn-link p-0 text-decoration-none" v-on:click="copyValue(data.item.accessCode)">
            {{ data.item.accessCode }}
          </button>
        </template>
        <template #cell(combinationResult)="data">
          <button class="btn btn-link p-0 text-decoration-none" v-on:click="copyValue(data.item.combinationResult)">
            {{ data.item.combinationResult }}
          </button>
        </template>
        <template #cell(phoneNumber)="data">
          <button class="btn btn-link p-0 text-decoration-none" v-on:click="copyValue(formatPhoneNumber(data.item.phoneNumber))">
            {{ formatPhoneNumber(data.item.phoneNumber) }}
          </button>
        </template>
        <template #cell(confirmed)="data">
          <button
            v-if="isSuperAdmin"
            class="btn btn-sm"
            :class="data.item.confirmed ? 'btn-success' : 'btn-outline-secondary'"
            v-on:click="setConfirmed(data.item)"
          >
            {{ data.item.confirmed ? '확정됨' : '확정하기' }}
          </button>
        </template>
      </b-table>
    </div>

    <AdminModal
      name="kakaoResultSettingModal"
      modal-title="결과값 세팅하기"
      modal-size="lg"
      :is-open="isSettingModalOpen"
      :close="closeSettingModal"
      :confirm="applyResultFormat"
      confirm-button-title="적용"
      :confirm-disabled="draftFormatComponents.length === 0"
    >
        <div class="font-semibold mb-2">선택 가능한 항목</div>
        <div class="flex flex-wrap gap-2 mt-3">
          <button
            v-for="component in formatComponentItems"
            :key="component.value"
            class="btn btn-sm btn-outline-secondary"
            v-on:click="appendFormatComponent(component.value)"
          >
            {{ component.label }}
          </button>
        </div>

        <div class="font-semibold mt-4 mb-2">결과값 형식</div>
        <div class="border rounded p-2 min-h-[56px]">
          <div
            v-if="draftFormatComponents.length === 0"
            class="text-gray-500 p-2"
          >
            항목을 선택하세요.
          </div>
          <div
            v-for="(component, index) in draftFormatComponents"
            :key="component.id"
            class="flex items-center justify-between border rounded px-3 py-2 mb-2 bg-white cursor-move"
            draggable="true"
            v-on:dragstart="onDragStart(index)"
            v-on:dragover.prevent
            v-on:drop="onDrop(index)"
          >
            <div class="flex items-center gap-2 flex-1">
              <span class="text-gray-400 mr-2">☰</span>
              <span class="whitespace-nowrap">{{ index + 1 }}. {{ formatComponentLabelMap[component.type] }}</span>
              <b-form-input
                v-if="component.type === 'CUSTOM'"
                v-model="component.value"
                size="sm"
                placeholder="입력하지 않으면 공백"
                class="ml-2"
              />
            </div>
            <button class="btn btn-sm btn-outline-danger" v-on:click="removeFormatComponent(index)">삭제</button>
          </div>
        </div>

        <div class="bg-gray-100 rounded p-3 mt-4">
          <div class="text-gray-500 mb-1">미리보기</div>
          <div class="font-bold">{{ previewResult }}</div>
        </div>

        <div class="flex justify-end mt-4">
          <button class="btn btn-outline-secondary" v-on:click="resetDefaultOptions">기본값</button>
        </div>
    </AdminModal>
  </div>
</template>

<script>
import api from "@/api/api"
import moment from "moment";
import Button from "@/components/Button.vue";
import {ButtonTypes} from "@/util/enums";
import AdminModal from "@/components/modals/AdminModal.vue";

export default {
  components: {AdminModal, Button},
  computed: {
    ButtonTypes() {
      return ButtonTypes
    },
    selectedOptionLabels() {
      return this.formatComponents.map((component) => this.getComponentDisplayLabel(component))
    },
    previewResult() {
      return this.buildPreviewResult(this.draftFormatComponents)
    },
  },
  data() {
    const defaultResultFormat = '${이름}${직접 입력한 값, 없으면 공백}${핸드폰 뒤 4자리}'
    const defaultFormatComponentTypes = ['NAME', 'CUSTOM', 'PHONE_LAST4']
    return {
      selectedDate: [
        moment().subtract(5, 'days').startOf('day').toDate(),
        moment().add(5, 'days').endOf('day').toDate()
      ],
      keyword: "",
      items: [],
      loading: {
        excel: false,
      },
      isSuperAdmin: localStorage.getItem("IS_SUPER_ADMIN") === "true",
      isSettingModalOpen: false,
      fields: [
        {
          label: '신청일자',
          key: 'createdAt',
        },
        {
          label: '학생 코드',
          key: 'accessCode',
        },
        {
          label: '카톡 이름',
          key: 'combinationResult',
        },
        {
          label: '연락처',
          key: 'phoneNumber',
        },
        ...(localStorage.getItem("IS_SUPER_ADMIN") === "true" ? [{
          label: '확정 여부',
          key: 'confirmed',
        }] : []),
      ],
      storageKey: 'KAKAO_MANAGEMENT_RESULT_FORMAT_SETTING',
      defaultResultFormat,
      defaultFormatComponentTypes,
      resultFormat: defaultResultFormat,
      formatComponentSeq: 3,
      formatComponents: [
        {id: 1, type: 'NAME', value: ''},
        {id: 2, type: 'CUSTOM', value: ''},
        {id: 3, type: 'PHONE_LAST4', value: ''},
      ],
      draftFormatComponents: [
        {id: 1, type: 'NAME', value: ''},
        {id: 2, type: 'CUSTOM', value: ''},
        {id: 3, type: 'PHONE_LAST4', value: ''},
      ],
      draggingComponentIndex: null,
      formatComponentItems: [
        {value: 'NAME', label: '이름'},
        {value: 'CUSTOM', label: '년월'},
        {value: 'PHONE_LAST4', label: '핸드폰 뒤 4자리'},
      ],
      formatComponentLabelMap: {
        NAME: '이름',
        CUSTOM: '년월',
        PHONE_LAST4: '핸드폰 뒤 4자리',
      },
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
    this.restoreResultFormatSetting()
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    buildParams() {
      const params = {
        keyword: this.keyword,
        format: this.resultFormat,
        startedAt: null,
        endedAt: null,
      }

      if (this.selectedDate?.length > 1) {
        if (this.selectedDate?.[0]) {
          params.startedAt = moment(this.selectedDate?.[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
        }
        if (this.selectedDate?.[1]) {
          params.endedAt = moment(this.selectedDate?.[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
        }
      }

      return params
    },
    search() {
      api.requestConsulting.searchStudentCodes(this.buildParams(), (response) => {
        this.items = response.data
      }, (err) => {
        console.log(err)
      })
    },
    downloadExcel() {
      this.loading.excel = true
      api.requestConsulting.downloadStudentCodesExcel(this.buildParams(), (response) => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `카톡관리_${new Date().toISOString()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        this.loading.excel = false
      }, (err) => {
        console.log(err)
        this.loading.excel = false
      })
    },
    setConfirmed(item) {
      api.requestConsulting.setConfirmed({id: item.id}, () => {
        this.items = this.items.map((target) => (
          target.id === item.id ? {...target, confirmed: !target.confirmed} : target
        ))
      }, (err) => {
        console.log(err)
      })
    },
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) {
        return ''
      }
      const numbers = phoneNumber.replace(/[^0-9]/g, '')
      if (numbers.length === 11) {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`
      }
      if (numbers.length === 10) {
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`
      }
      return phoneNumber
    },
    formatCreatedAt(createdAt) {
      if (!createdAt) {
        return ''
      }
      return moment(createdAt).format('YYYY-MM-DD HH:mm:ss')
    },
    copyValue(value) {
      if (!value) {
        return
      }
      navigator.clipboard.writeText(value)
      this.$toast.show('복사되었습니다.', {
        position: "top-right",
        duration: 1200
      });
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && this.isSettingModalOpen) {
        this.closeSettingModal()
      }
    },
    openSettingModal() {
      this.draftFormatComponents = this.cloneFormatComponents(this.formatComponents)
      this.isSettingModalOpen = true
    },
    closeSettingModal() {
      this.isSettingModalOpen = false
    },
    applyResultFormat() {
      this.formatComponents = this.cloneFormatComponents(this.draftFormatComponents)
      this.resultFormat = this.buildResultFormat(this.formatComponents)
      this.saveResultFormatSetting()
      this.isSettingModalOpen = false
      // this.search()
    },
    resetDefaultOptions() {
      this.draftFormatComponents = this.createFormatComponents(this.defaultFormatComponentTypes)
    },
    appendFormatComponent(componentValue) {
      this.draftFormatComponents = [...this.draftFormatComponents, this.createFormatComponent(componentValue)]
    },
    removeFormatComponent(index) {
      this.draftFormatComponents = this.draftFormatComponents.filter((_, targetIndex) => targetIndex !== index)
    },
    onDragStart(index) {
      this.draggingComponentIndex = index
    },
    onDrop(dropIndex) {
      if (this.draggingComponentIndex === null || this.draggingComponentIndex === dropIndex) {
        return
      }

      const orderedComponents = [...this.draftFormatComponents]
      const [draggedComponent] = orderedComponents.splice(this.draggingComponentIndex, 1)
      orderedComponents.splice(dropIndex, 0, draggedComponent)
      this.draftFormatComponents = orderedComponents
      this.draggingComponentIndex = null
    },
    createFormatComponent(type, value = '') {
      this.formatComponentSeq += 1
      return {id: this.formatComponentSeq, type, value}
    },
    createFormatComponents(types) {
      return types.map((type) => this.createFormatComponent(type))
    },
    cloneFormatComponents(components) {
      return components.map((component) => this.createFormatComponent(component.type, component.value))
    },
    saveResultFormatSetting() {
      localStorage.setItem(this.storageKey, JSON.stringify({
        components: this.formatComponents.map((component) => ({
          type: component.type,
          value: component.value || '',
        })),
      }))
    },
    restoreResultFormatSetting() {
      const storedSetting = localStorage.getItem(this.storageKey)
      if (!storedSetting) {
        return
      }

      try {
        const parsedSetting = JSON.parse(storedSetting)
        const storedComponents = Array.isArray(parsedSetting?.components)
          ? parsedSetting.components
          : []
        const availableTypes = this.formatComponentItems.map((component) => component.value)
        const restoredComponents = storedComponents
          .filter((component) => availableTypes.includes(component.type))
          .map((component) => this.createFormatComponent(component.type, component.value || ''))

        if (restoredComponents.length === 0) {
          return
        }

        this.formatComponents = restoredComponents
        this.draftFormatComponents = this.cloneFormatComponents(restoredComponents)
        this.resultFormat = this.buildResultFormat(restoredComponents)
      } catch (e) {
        localStorage.removeItem(this.storageKey)
      }
    },
    getComponentDisplayLabel(component) {
      if (component.type !== 'CUSTOM') {
        return this.formatComponentLabelMap[component.type]
      }
      return component.value ? `${this.formatComponentLabelMap[component.type]}(${component.value})` : this.formatComponentLabelMap[component.type]
    },
    getFormatComponentToken(component) {
      if (component.type === 'NAME') return '${이름}'
      if (component.type === 'YEAR_MONTH') return '${년월}'
      if (component.type === 'YEAR_MONTH_DAY') return '${년월일}'
      if (component.type === 'CUSTOM') return component.value || '${직접 입력한 값, 없으면 공백}'
      return '${핸드폰 뒤 4자리}'
    },
    buildResultFormat(components) {
      const format = components.map((component) => (
        this.getFormatComponentToken(component)
      )).join('')
      return format || this.defaultResultFormat
    },
    buildPreviewResult(components) {
      return this.buildResultFormat(components)
        .replaceAll('${이름}', '홍길동')
        .replaceAll('${년월}', moment().format('YYMM'))
        .replaceAll('${년월일}', moment().format('YYMMDD'))
        .replaceAll('${직접 입력한 값, 없으면 공백}', '')
        .replaceAll('${핸드폰 뒤 4자리}', '1234')
    },
  }
}
</script>
